/**
 * Annotation Export Page
 * Extract messages from dora_sessions and create annotation batches
 */

import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Database, BarChart3, FolderOpen } from 'lucide-react';
import { ExportPreview, useExportPreview, useExecuteExport } from '@/features/annotations';
import type { ExportFilters, ExportPreviewResponse } from '@/types/annotation';

// Sub-navigation tabs
const TABS = [
  { path: '/annotation/batches', label: '批次管理', icon: FolderOpen },
  { path: '/annotation/export', label: '数据导出', icon: Database },
  { path: '/annotation/stats', label: '统计报表', icon: BarChart3 },
];

export function AnnotationExportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [previewData, setPreviewData] = useState<ExportPreviewResponse | null>(null);

  const previewMutation = useExportPreview();
  const executeMutation = useExecuteExport();

  const handlePreview = async (filters: ExportFilters, sampleSize: number, offset: number = 0, excludeExported: boolean = false) => {
    try {
      const result = await previewMutation.mutateAsync({
        filters,
        sample_size: sampleSize,
        offset,
        exclude_exported: excludeExported,
      });
      setPreviewData(result);
    } catch (error) {
      console.error('Preview failed:', error);
      alert('预览失败: ' + (error as Error).message);
    }
  };

  const handleExecute = async (
    filters: ExportFilters,
    sampleSize: number,
    title: string,
    description?: string,
    selectedIds?: string[],
    skipDuplicates?: boolean
  ) => {
    try {
      const result = await executeMutation.mutateAsync({
        filters,
        sample_size: sampleSize,
        batch_title: title,
        batch_description: description,
        selected_ids: selectedIds,
        skip_duplicates: skipDuplicates ?? true,
      });

      alert(`成功创建批次 ${result.batch_code}，包含 ${result.tasks_created} 条任务`);
      navigate('/annotation/batches');
    } catch (error) {
      console.error('Export failed:', error);
      alert('导出失败: ' + (error as Error).message);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">数据标注</h1>

        {/* Sub-navigation Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.path;
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`
                  flex items-center gap-2 px-4 py-2 -mb-px border-b-2 transition-colors
                  ${isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Page Description */}
      <div className="mb-6">
        <p className="text-gray-600">从 dora_sessions 中提取用户消息，创建标注任务</p>
      </div>

      {/* Export Preview Component */}
      <ExportPreview
        onPreview={handlePreview}
        onExecute={handleExecute}
        previewData={previewData || undefined}
        isLoading={previewMutation.isPending}
        isExecuting={executeMutation.isPending}
      />
    </div>
  );
}
